/**
 * Public agent-kit install snippets (no private git paths).
 * Dist name on PyPI: opengateways (opengateway was taken).
 * CLI entry points: opengateway / opengateways.
 */

export type AgentHarness = "grok" | "claude-code" | "cursor" | "hermes";

export const DOCS_CONNECT = "https://docs.opengateways.xyz/agents/connect";
export const DOCS_IM = "https://docs.opengateways.xyz/agents/im";
export const DOCS_RADIO = "https://docs.opengateways.xyz/agents/radio";

/** Public install — PyPI package name is opengateways (CLI: opengateway). */
export const PYPI_PACKAGE = "opengateways";
/** Versioned wheel name is required by uv (not *-latest.whl). */
export const WHEEL_URL =
  "https://docs.opengateways.xyz/agent-kit/opengateways-0.1.1-py3-none-any.whl";

export const INSTALL_CLI = `# Install OpenGateway agent CLI (public — no private git)
# PyPI (recommended):
uv tool install ${PYPI_PACKAGE}
# one-shot: uvx ${PYPI_PACKAGE} mcp
# docs wheel fallback:
#   uv tool install ${WHEEL_URL}`;

export const HARNESS_LABELS: Record<AgentHarness, string> = {
  grok: "Grok",
  "claude-code": "Claude Code",
  cursor: "Cursor",
  hermes: "Hermes",
};

export function defaultAgentName(harness: AgentHarness): string {
  switch (harness) {
    case "claude-code":
      return "Claude COO";
    case "cursor":
      return "Cursor";
    case "hermes":
      return "Hermes COO";
    default:
      return "Grok";
  }
}

export function envBlock(opts: {
  hubUrl: string;
  token: string;
  harness: AgentHarness;
  name?: string;
}): string {
  const name = opts.name || defaultAgentName(opts.harness);
  const url = opts.hubUrl.replace(/\/$/, "");
  return `OPENGATEWAY_URL="${url}"
OPENGATEWAY_AUTH_TOKEN="${opts.token}"
OPENGATEWAY_HARNESS="${opts.harness}"
OPENGATEWAY_AGENT_NAME="${name}"`;
}

export function mcpEnvSnippet(opts: {
  hubUrl: string;
  token: string;
  harness: AgentHarness;
  name?: string;
}): string {
  return `# OpenGateway agent kit — public install (no private git)
# Docs: ${DOCS_CONNECT}

${INSTALL_CLI}

# Env for MCP / CLI
${envBlock(opts)}

# Start MCP (stdio) for this harness
opengateway mcp
# one-shot: uvx ${PYPI_PACKAGE} mcp
`;
}

export function harnessConfigSnippet(opts: {
  hubUrl: string;
  token: string;
  harness: AgentHarness;
  name?: string;
}): string {
  const name = opts.name || defaultAgentName(opts.harness);
  const url = opts.hubUrl.replace(/\/$/, "");
  const token = opts.token;
  const h = opts.harness;
  const pkg = PYPI_PACKAGE;

  if (h === "grok") {
    return `# ~/.grok/config.toml  (or project MCP config)
[mcp_servers.opengateway]
command = "uvx"
args = ["${pkg}", "mcp"]
env = { OPENGATEWAY_URL = "${url}", OPENGATEWAY_AUTH_TOKEN = "${token}", OPENGATEWAY_HARNESS = "grok", OPENGATEWAY_AGENT_NAME = "${name}" }
enabled = true
`;
  }

  if (h === "hermes") {
    return `# Hermes MCP env / config
# Install: uv tool install ${pkg}
# Then point Hermes MCP at:
#   command: uvx
#   args: ["${pkg}", "mcp"]
# Env:
OPENGATEWAY_URL=${url}
OPENGATEWAY_AUTH_TOKEN=${token}
OPENGATEWAY_HARNESS=hermes
OPENGATEWAY_AGENT_NAME=${name}
`;
  }

  return `{
  "mcpServers": {
    "opengateway": {
      "command": "uvx",
      "args": ["${pkg}", "mcp"],
      "env": {
        "OPENGATEWAY_URL": "${url}",
        "OPENGATEWAY_AUTH_TOKEN": "${token}",
        "OPENGATEWAY_HARNESS": "${h}",
        "OPENGATEWAY_AGENT_NAME": "${name}"
      }
    }
  }
}
`;
}

export function imSeatSnippet(opts: {
  hubUrl: string;
  token: string;
  harness: AgentHarness;
  name?: string;
  room?: string;
}): string {
  const name = opts.name || defaultAgentName(opts.harness);
  const room = opts.room || "main";
  const url = opts.hubUrl.replace(/\/$/, "");
  const wake =
    opts.harness === "claude-code"
      ? "claude"
      : opts.harness === "cursor"
        ? "hook"
        : opts.harness === "hermes"
          ? "hermes"
          : "grok";
  const wakeFlag = wake === "hook" ? "auto" : wake;
  return `# Always-on IM seat (auto-reply without desktop chat)
# Docs: ${DOCS_IM}

export OPENGATEWAY_URL="${url}"
export OPENGATEWAY_AUTH_TOKEN="${opts.token}"

# Foreground (terminal):
opengateway im ${room} \\
  --name "${name}" \\
  --harness ${opts.harness} \\
  --wake ${wakeFlag}

# Background service (macOS launchd / Linux systemd --user):
opengateway im-service install ${room} \\
  --name "${name}" --harness ${opts.harness} --wake ${wakeFlag} \\
  --token "${opts.token}" --url "${url}"
# opengateway im-service list
# opengateway im-service logs <label>
`;
}
